var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import Big from 'big.js';
import { useChains } from '.';
import { useWallet } from '../providers';
import { isRouteDone } from '../stores';
import { useTokenBalance } from './useTokenBalance';
const refetchInterval = 30000;
export const useGasSufficiency = (route) => {
    const { account } = useWallet();
    const { getChainById } = useChains();
    const { token: fromToken, getTokenBalancesWithRetry } = useTokenBalance(route === null || route === void 0 ? void 0 : route.fromToken);
    const { data: insufficientGas, isInitialLoading: insufficientGasLoading } = useQuery(['gas-sufficiency-check', account.address, route === null || route === void 0 ? void 0 : route.id], () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!account.address || !route) {
            return null;
        }
        const gasCosts = route.steps
            .filter((step) => !step.execution || step.execution.status !== 'DONE')
            .reduce((groupedGasCosts, step) => {
            if (step.estimate.gasCosts) {
                const { token } = step.estimate.gasCosts[0];
                const gasCostAmount = step.estimate.gasCosts
                    .reduce((amount, gasCost) => amount.plus(Big(gasCost.amount || 0)), Big(0))
                    .div(Math.pow(10, token.decimals));
                const groupedGasCost = groupedGasCosts[token.chainId];
                const gasAmount = groupedGasCost
                    ? groupedGasCost.gasAmount.plus(gasCostAmount)
                    : gasCostAmount;
                groupedGasCosts[token.chainId] = {
                    gasAmount,
                    tokenAmount: gasAmount,
                    token,
                };
                return groupedGasCosts;
            }
            return groupedGasCosts;
        }, {});
        if (route.fromToken.address === ((_a = gasCosts[route.fromChainId]) === null || _a === void 0 ? void 0 : _a.token.address)) {
            gasCosts[route.fromChainId].tokenAmount = (_b = gasCosts[route.fromChainId]) === null || _b === void 0 ? void 0 : _b.gasAmount.plus(Big(route.fromAmount).div(Math.pow(10, route.fromToken.decimals)));
        }
        const tokenBalances = yield getTokenBalancesWithRetry(account.address, Object.values(gasCosts).map((item) => item.token));
        if (!(tokenBalances === null || tokenBalances === void 0 ? void 0 : tokenBalances.length)) {
            return null;
        }
        [route.fromChainId, route.toChainId].forEach((chainId) => {
            var _a, _b, _c, _d, _e, _f;
            if (gasCosts[chainId]) {
                const gasTokenBalance = Big((_b = (_a = tokenBalances === null || tokenBalances === void 0 ? void 0 : tokenBalances.find((t) => t.chainId === gasCosts[chainId].token.chainId &&
                    t.address === gasCosts[chainId].token.address)) === null || _a === void 0 ? void 0 : _a.amount) !== null && _b !== void 0 ? _b : 0);
                const insufficient = gasTokenBalance.lte(0) ||
                    gasTokenBalance.lt((_c = gasCosts[chainId].gasAmount) !== null && _c !== void 0 ? _c : Big(0)) ||
                    gasTokenBalance.lt((_d = gasCosts[chainId].tokenAmount) !== null && _d !== void 0 ? _d : Big(0));
                const insufficientAmount = insufficient
                    ? (_f = (_e = gasCosts[chainId].tokenAmount) === null || _e === void 0 ? void 0 : _e.minus(gasTokenBalance)) !== null && _f !== void 0 ? _f : gasCosts[chainId].gasAmount.minus(gasTokenBalance)
                    : undefined;
                gasCosts[chainId] = Object.assign(Object.assign({}, gasCosts[chainId]), { insufficient, insufficientAmount: insufficientAmount === null || insufficientAmount === void 0 ? void 0 : insufficientAmount.round(5, Big.roundUp), chain: insufficient ? getChainById(chainId) : undefined });
            }
        });
        const gasCostResult = Object.values(gasCosts).filter((gasCost) => gasCost.insufficient);
        return gasCostResult;
    }), {
        enabled: Boolean(account.address && route),
        refetchInterval,
        staleTime: refetchInterval,
        cacheTime: refetchInterval,
    });
    const { data: insufficientFunds, isInitialLoading: insufficientFundsLoading, } = useQuery(['funds-sufficiency-check', account.address, route === null || route === void 0 ? void 0 : route.id], () => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e, _f;
        if (!account.address || !fromToken || !route || isRouteDone(route)) {
            return null;
        }
        let currentTokenBalance = Big((_c = fromToken === null || fromToken === void 0 ? void 0 : fromToken.amount) !== null && _c !== void 0 ? _c : 0);
        const currentAction = (_d = route.steps.filter((step) => !step.execution || step.execution.status !== 'DONE')[0]) === null || _d === void 0 ? void 0 : _d.action;
        if (fromToken.chainId === currentAction.fromToken.chainId &&
            fromToken.address === currentAction.fromToken.address &&
            currentTokenBalance.gt(0)) {
            const insufficientFunds = Big(route.fromAmount)
                .div(Math.pow(10, route.fromToken.decimals))
                .gt(currentTokenBalance);
            return insufficientFunds;
        }
        const tokenBalances = yield getTokenBalancesWithRetry(account.address, [
            currentAction.fromToken,
        ]);
        currentTokenBalance = Big((_f = (_e = tokenBalances === null || tokenBalances === void 0 ? void 0 : tokenBalances[0]) === null || _e === void 0 ? void 0 : _e.amount) !== null && _f !== void 0 ? _f : 0);
        const insufficientFunds = Big(currentAction.fromAmount)
            .div(Math.pow(10, currentAction.fromToken.decimals))
            .gt(currentTokenBalance);
        return insufficientFunds;
    }), {
        enabled: Boolean(account.address && route && fromToken),
        refetchInterval,
        staleTime: refetchInterval,
        cacheTime: refetchInterval,
    });
    return {
        insufficientGas,
        insufficientFunds,
        isLoading: insufficientGasLoading || insufficientFundsLoading,
    };
};
